import { Box, CircularProgress } from '@mui/material';
import React from 'react';
import AssetImage from '../AssetImage';
import {
  IMAGE_ASPECT_RATIO,
  IMAGE_FILLS_EMPTY_CARD,
  IMAGE_INSET,
  IMAGE_RADIUS,
} from 'DesignTokens';
import { TEMPORARILY_DISABLE_IMAGE_EXPANSION } from './propertyCardConstants';

interface PropertyCardImageSectionProps {
  derivedImage: string;
  altText: string | undefined;
  derivedTitle: string | undefined;
  mini: boolean;
  isExpanded: boolean;
  imageNaturalHeight: number | null;
  imageAvailable: boolean;
  preventImageCrop: boolean | undefined;
  printMode: boolean | undefined;
  hasDescriptionContent: boolean;
  flush?: boolean;
  customImageRenderer?: () => React.ReactNode;
  onImageLoad: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  onToggleInPlaceExpanded: () => void;
  onExpand?: () => void;
}

export default function PropertyCardImageSection({
  derivedImage,
  altText,
  derivedTitle,
  mini,
  isExpanded,
  imageNaturalHeight,
  imageAvailable,
  preventImageCrop,
  printMode,
  hasDescriptionContent,
  flush = false,
  customImageRenderer,
  onImageLoad,
  onToggleInPlaceExpanded,
  onExpand,
}: PropertyCardImageSectionProps) {
  const insetX = flush ? '0px' : mini ? IMAGE_INSET.sizeMini : IMAGE_INSET.size;
  const insetTop = flush ? '0px' : mini ? IMAGE_INSET.topMini : IMAGE_INSET.top;
  const imageStyles = {
    position: 'absolute',
    top: insetTop,
    left: insetX,
    // Explicit width/height (not right/bottom) — a replaced element like
    // <img> positioned absolutely with all four insets set but no explicit
    // size falls back to its own intrinsic dimensions in some browsers
    // instead of filling the inset box, which crops into a random corner
    // of the source image rather than covering the intended area.
    width: `calc(100% - ${insetX} - ${insetX})`,
    height: `calc(100% - ${insetTop})`,
    objectFit: preventImageCrop ? 'contain' : 'cover',
    borderRadius: flush ? 0 : IMAGE_RADIUS,
    transition: 'all 0.4s ease-in-out',
  };

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          ...(isExpanded && !TEMPORARILY_DISABLE_IMAGE_EXPANSION && imageNaturalHeight
            ? { paddingTop: 0, height: `${Math.min(imageNaturalHeight, 400)}px` }
            : IMAGE_ASPECT_RATIO
              ? { aspectRatio: IMAGE_ASPECT_RATIO, height: 'auto' }
              : { paddingTop: '56.25%', height: 0 }),
          overflow: 'hidden',
          flex: (IMAGE_FILLS_EMPTY_CARD || flush) && !hasDescriptionContent ? 1 : undefined,
          transition: 'padding-top 0.4s ease-in-out, height 0.4s ease-in-out',
        }}
      >
        {customImageRenderer ? (
          customImageRenderer()
        ) : (
          <AssetImage
            url={derivedImage}
            alt={altText || derivedTitle}
            printMode={printMode}
            onClick={(e) => {
              e.stopPropagation();
              if (!TEMPORARILY_DISABLE_IMAGE_EXPANSION) {
                onToggleInPlaceExpanded();
              }
              onExpand?.();
            }}
            onLoad={onImageLoad}
            sx={imageStyles}
          />
        )}
      </Box>
      {!imageAvailable && (
        <Box sx={imageStyles}>
          <CircularProgress />
        </Box>
      )}
    </>
  );
}
